import { Seo } from '@/components/Seo';
import { JsonLd, makeHowToSchema, makeFAQSchema, makeBreadcrumbSchema } from '@/components/Schema';
import { FAQ } from '@/components/FAQ';
import { FinalCTA } from '@/components/FinalCTA';
import { Link } from 'react-router-dom';
import { Sparkles, PenTool, Wand2, CheckCircle, Briefcase, GraduationCap, Repeat } from 'lucide-react';

const steps = [
  'Enter your job title and paste a few bullet points from your resume.',
  'Click the AI Rewrite button to get stronger, action-driven bullet suggestions.',
  'Pick a template and download your resume as an ATS-friendly PDF.',
];

const faqs = [
  {
    question: 'How does the AI resume writer work?',
    answer: 'You type a bullet point describing your experience, and our AI rewrites it into a stronger, achievement-focused statement. Free users get 2 AI rewrites per browser ID.',
  },
  {
    question: 'Can I edit the AI suggestions?',
    answer: 'Yes. The AI gives you a suggestion that you can accept, edit, or discard before adding it to your resume.',
  },
  {
    question: 'Will the AI write my entire resume?',
    answer: 'It helps you rewrite individual bullets one at a time. You remain in control of every section and every word.',
  },
  {
    question: 'Is AI generation free?',
    answer: 'Yes. You get 2 free AI rewrites per browser ID. Upgrade to Pro for 50 rewrites per day.',
  },
  {
    question: 'Is my data used to train AI models?',
    answer: 'No. We do not use your resume data to train AI models. We only send the specific bullet text to our AI provider when you click AI Rewrite.',
  },
];

export function AiResumeBuilder() {
  return (
    <>
      <Seo
        title="AI Resume Builder | Free, No Sign-Up, Instant PDF Download"
        description="Use AI to rewrite resume bullets into stronger, achievement-focused statements. No sign-up required. 2 free AI rewrites per browser ID."
        path="/ai-resume-builder"
      />
      <JsonLd schema={makeHowToSchema('How to build a resume with AI', 'Rewrite resume bullets with AI in three steps.', steps)} />
      <JsonLd schema={makeFAQSchema(faqs)} />
      <JsonLd schema={makeBreadcrumbSchema([{ name: 'AI Resume Builder', path: '/ai-resume-builder' }])} />

      <section className="hero-glow py-24 md:py-32">
        <div className="mx-auto max-w-[800px] px-6 text-center">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-[#00D4AA]/15">
            <Sparkles size={32} className="text-[#00D4AA]" />
          </div>
          <h1 className="text-balance mt-6 text-4xl font-extrabold text-white md:text-5xl">
            AI Resume Builder — Create a Professional Resume Free
          </h1>
          <p className="mx-auto mt-6 max-w-[680px] text-lg text-white/75">
            Our AI resume builder writes tailored resume bullets based on your job title and description. It is free, requires no account, and exports a PDF instantly. 2 free AI generations per browser ID.
          </p>
          <Link
            to="/app/create"
            className="btn-primary mt-8 inline-flex items-center justify-center rounded-[10px] px-8 py-4 text-base font-semibold"
          >
            Try AI Resume Builder
          </Link>
        </div>
      </section>

      <section className="bg-[#0A2540] py-16 md:py-24">
        <div className="mx-auto max-w-[1200px] px-6">
          <div className="text-center">
            <span className="rounded-full bg-[#00D4AA]/15 px-4 py-1.5 text-sm font-semibold text-[#00D4AA]">
              Who it is for
            </span>
            <h2 className="mt-4 text-3xl font-bold text-white md:text-4xl">
              Built for every career stage
            </h2>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {[
              { icon: Briefcase, title: 'Job Seekers', desc: 'Turn daily tasks into quantified achievements that hiring managers notice.' },
              { icon: Repeat, title: 'Career Changers', desc: 'Reframe your past experience to match your new target role.' },
              { icon: GraduationCap, title: 'Students', desc: 'Make coursework, projects, and part-time jobs sound professional.' },
            ].map((c) => (
              <div key={c.title} className="glass-card rounded-2xl p-6 transition duration-300 hover:-translate-y-1 hover:border-[rgba(0,212,170,0.35)]">
                <div className="flex h-12 w-12 items-center justify-center rounded-[10px] bg-[#00D4AA]/15">
                  <c.icon size={24} className="text-[#00D4AA]" />
                </div>
                <h3 className="mt-4 text-lg font-semibold text-white">{c.title}</h3>
                <p className="mt-2 text-[15px] text-white/75">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-20 md:py-28">
        <div className="mx-auto max-w-[1200px] px-6">
          <div className="text-center">
            <span className="rounded-full bg-[#E7F1FA] px-4 py-1.5 text-sm font-semibold text-[#0A2540]">
              How it works
            </span>
            <h2 className="mt-4 text-3xl font-bold text-[#0A2540] md:text-4xl">
              Three steps to better bullets
            </h2>
          </div>
          <div className="mt-12 grid gap-8 md:grid-cols-3">
            {[
              { icon: PenTool, title: '1. Add a bullet', desc: 'Type your current experience in plain English.' },
              { icon: Wand2, title: '2. Rewrite with AI', desc: 'Click the AI Rewrite button to get stronger, measurable phrasing.' },
              { icon: CheckCircle, title: '3. Download PDF', desc: 'Pick a template and export your resume as a PDF.' },
            ].map((s) => (
              <div key={s.title} className="rounded-2xl border border-[#E2E8F0] bg-[#F8FAFC] p-6 text-center transition duration-300 hover:bg-white hover:shadow-sm">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-lg bg-white p-3 shadow-sm">
                  <s.icon size={24} className="text-[#00D4AA]" />
                </div>
                <h3 className="mt-4 text-lg font-semibold text-[#0A2540]">{s.title}</h3>
                <p className="mt-2 text-[#4A5568]">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#F6F9FC] py-16">
        <div className="mx-auto max-w-[800px] px-6">
          <div className="text-center">
            <span className="rounded-full bg-[#E7F1FA] px-4 py-1.5 text-sm font-semibold text-[#0A2540]">
              FAQ
            </span>
            <h2 className="mt-4 text-3xl font-bold text-[#0A2540]">
              AI Resume Builder FAQ
            </h2>
          </div>
          <div className="mt-10">
            <FAQ items={faqs} />
          </div>
        </div>
      </section>

      <FinalCTA
        title="Start writing better bullets with AI"
        subtitle="Free to use. No account required."
        buttonText="Try AI Resume Builder"
        buttonTo="/app/create"
      />
    </>
  );
}

export default AiResumeBuilder;
